import React from 'react';
import { Link } from 'react-router-dom';
import './TermsOfService.css';

function TermsOfService() {
  return (
    <div className="terms-page">
      <div className="terms-container">

        <header className="terms-header">
          <h1>Terms of Service</h1>
          <p className="last-updated">Last Updated: December 2025</p>
        </header>

        <section className="terms-content">
          <div className="terms-intro">
            <h2>Welcome to Reda Labs, LLC</h2>
            <p>
              The Reda Labs, LLC branded and related services are provided by Reda Labs, LLC ("Reda Labs") through the Reda Labs, LLC Website (the "Site") <a href="https://www.redalabs.com" target="_blank" rel="noopener noreferrer">www.redalabs.com</a> to you ("you" or "User"). This Terms of Service Agreement ("Agreement") is entered into by and between Reda Labs, LLC and you, effective as of the date of your use of this website or the date of electronic acceptance.
            </p>
            <p>
              By using this Site, the services available at this Site, and any service, software, application, plug-in, component, functionality, or program created by Reda Labs, LLC (together, the "Services"), you agree to the following terms and conditions of the Agreement and <Link to="/privacy">Privacy Policy</Link> ("Privacy Policy"). This Agreement, along with any other policies or guidelines posted on this Site, shall govern your use of this Site and the Services, whether directly or through a third-party site. Please read them carefully before using this Site or the Services.
            </p>
          </div>

          <div className="terms-section">
            <h2>Terminology</h2>
            <p>
              The terms "we", "us" or "our" refer to Reda Labs, LLC. The terms "you", "your", "User" or "customer" refer to any individual or entity who accepts this Agreement, has access to your account, or uses the Services. Nothing in this Agreement shall confer any third-party rights or benefits.
            </p>
            <p>
              By visiting this Site and/or completing the registration process for the Services, you represent and warrant that you have read, understand, and have the legal capacity to be legally bound by this Agreement.
            </p>
          </div>

          <div className="terms-section">
            <h2>User Eligibility</h2>
            <p>By using this Site or the Services, you represent and warrant that you are:</p>
            <ul>
              <li>At least eighteen (18) years of age;</li>
              <li>Otherwise recognized as being able to form legally binding contracts under applicable law;</li>
              <li>Not a person barred from purchasing or receiving the Services under U.S. or other applicable jurisdiction laws.</li>
            </ul>
            <p>
              If you enter this Agreement on behalf of a corporate entity, you represent and warrant that you have the legal authority to bind that entity to the terms and conditions contained in this Agreement. If Reda Labs, LLC finds that you do not have the legal authority to bind the corporate entity, you will be personally responsible for the obligations contained in this Agreement. Reda Labs, LLC shall not be liable for any loss or damage resulting from its reliance on any instruction, notice, document, or communication it reasonably believes originates from an authorized representative of your corporate entity.
            </p>
          </div>

          <div className="terms-section">
            <h2>Modification of Agreement, Site, or Services</h2>
            <p>
              Reda Labs, LLC may, in its sole and absolute discretion, change or modify this Agreement at any time. Such modifications shall be effective immediately upon posting on the Site. Your use of the Services after changes are posted constitutes your acceptance of the revised Agreement. If you do not agree to be bound by the Agreement as last revised, do not use (or continue to use) the Services. Reda Labs, LLC reserves the right to modify, change, or discontinue any aspect of the Site or Services, including prices and fees, at any time.
            </p>
          </div>

          <div className="terms-section">
            <h2>Registration Obligations</h2>
            <p>
              To obtain and use the Services, you will be required to register with Reda Labs, LLC by completing a registration form and designating a user ID and password. When registering, you agree to:
            </p>
            <ul>
              <li>Provide true, accurate, current, and complete information as prompted by the Service's registration form (the "Registration Data")</li>
              <li>Maintain and promptly update the Registration Data to keep it true, accurate, current, and complete.</li>
            </ul>
            <p>
              If any provided information is untrue, inaccurate, not current, or incomplete, or if Reda Labs, LLC suspects so, we may suspend or terminate your account and refuse any current or future use of the Services (or any portion thereof).
            </p>
            <p>
              You may not authorize any third party to access or use the Services on your behalf. You are responsible for maintaining the confidentiality of your user ID and password and solely responsible for activities occurring on your account. You agree to immediately notify Reda Labs, LLC of any unauthorized use of your account or any security breach. Reda Labs, LLC cannot be liable for any loss or damage arising from unauthorized account use.
            </p>
          </div>

          <div className="terms-section">
            <h2>General Rules of Conduct</h2>
            <ul>
              <li>Comply with this Agreement, applicable Services Agreement or policy, and all applicable laws, rules, and regulations.</li>
              <li>Not collect or harvest any non-public or personally identifiable information about another User without their consent.</li>
              <li>Not use this Site or Services in any manner that: is illegal or encourages illegal activity; exploits children or promotes related materials; encourages terrorism, violence, spam, hacking, unauthorized use of prescription medication, infringement on intellectual property rights, or invasion of privacy.</li>
              <li>Not interfere with the operation of this Site or Services.</li>
              <li>Not copy, distribute, or alter any part of this Site or Services, except as expressly authorized by Reda Labs, LLC.</li>
            </ul>
          </div>

          <div className="terms-section">
            <h2>AI-Generated Content Disclaimer</h2>
            <p>
              The responses generated by Reda Labs, LLC's AI systems are for educational purposes only and should not be interpreted as legal, financial, or professional advice. Reda Labs, LLC disclaims all liability for any errors or omissions in content provided and any damages arising from AI-generated content usage. Users are encouraged to seek professional advice for their specific needs.
            </p>
          </div>

          <div className="terms-section">
            <h2>Academic Integrity Policy</h2>
            <p>
              Reda Labs, LLC is committed to upholding the highest standards of academic integrity. Our platform is designed to enhance learning, not substitute for academic efforts. The use of Reda Labs, LLC services to circumvent academic honesty policies is strictly prohibited and may result in termination of access to services.
            </p>
          </div>

          <div className="terms-section">
            <h2>Fees, Payments, and Automatic Renewal</h2>
            <p>
              Fees for Reda Labs, LLC services are available at <a href="https://www.redalabs.com" target="_blank" rel="noopener noreferrer">www.redalabs.com</a> and within the Services.
            </p>
            <p>
              <strong>Automatic Renewal:</strong> Unless agreed upon in writing, all services renew automatically for the same term and charge the user's payment method on file on the first day of the renewal term.
            </p>
            <p>
              <strong>Price Changes:</strong> Reda Labs, LLC reserves the right to change prices at any time, effective when posted online. Renewals will reflect current rates unless you disable automatic renewal in your account settings.
            </p>
          </div>

          <div className="terms-section">
            <h2>Monitoring of Content and Account Termination Policy</h2>
            <p>
              Reda Labs, LLC generally does not pre-screen User Content. However, Reda Labs, LLC reserves the right (but undertakes no duty) to decide whether any User Content is appropriate and complies with this Agreement. Reda Labs, LLC may remove any User Content and/or terminate a User's access to this Site or the Services found at this Site for posting or publishing any material in violation of this Agreement or for otherwise violating this Agreement, as determined by Reda Labs, LLC in its sole and absolute discretion, at any time and without prior notice. Reda Labs, LLC may also terminate a User's access to this Site or the Services if Reda Labs, LLC has reason to believe the User is a repeat offender. If Reda Labs, LLC terminates your access, it may, at its sole discretion, remove and destroy any data and files stored by you on its servers.
            </p>
          </div>

          <div className="terms-section">
            <h2>Discontinued Services and End of Life Policy</h2>
            <p>
              Reda Labs, LLC reserves the right to discontinue any of the Services at any time, for any reason, without prior notice. When a Service reaches End-of-Life (EOL), that product or service will no longer be supported by Reda Labs, LLC in any way. Reda Labs, LLC will attempt to notify you 30 days in advance of the EOL date and offer a comparable Service, prorated in-store credit, or refund.
            </p>
            <p>
              <strong>Notice and Migration:</strong> It is your responsibility to take steps to replace the Service by migrating to a new Service before the EOL date. Reda Labs, LLC will not be liable for any loss or damage arising from any such migration.
            </p>
          </div>

          <div className="terms-section">
            <h2>Third-Party Content, Sites, and Services</h2>
            <p>
              All transactions using Reda Labs, LLC's services are between the transacting parties only. The Services may contain links to third-party content, sites, or services. Reda Labs, LLC is not an agent of any transacting party nor directly involved in any such transaction. Any dealings with third-party content or services are solely between you and the applicable third party. Reda Labs, LLC disclaims all liability arising from your use of any third-party website.
            </p>
          </div>

          <div className="terms-section">
            <h2>Refund of Charges</h2>
            <p>
              Except as specifically set forth in this section, all Reda Labs, LLC services are prepaid for the selected period (monthly, yearly, etc.) and are non-refundable.
            </p>
            <ul>
              <li><strong>Free 7-Day Trial:</strong> You may cancel or downgrade your account free of charge within 7 days after your account is created.</li>
              <li><strong>Annual Subscribers:</strong> Non-trial annual subscribers may cancel or downgrade within 30 days of their billing date and receive a partial refund equivalent to 80% of the annual fee. Other requests for refunds may be processed at Reda Labs, LLC's sole discretion.</li>
            </ul>
          </div>

          <div className="terms-section">
            <h2>Disclaimer of Representations and Warranties</h2>
            <p>
              YOUR USE OF THIS SITE AND SERVICES SHALL BE AT YOUR OWN RISK. THIS SITE AND SERVICES ARE PROVIDED "AS IS," "AS AVAILABLE," AND "WITH ALL FAULTS." REDA LABS, LLC, ITS OFFICERS, DIRECTORS, EMPLOYEES, AND AGENTS DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING IMPLIED WARRANTIES OF TITLE, MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. NO ORAL OR WRITTEN INFORMATION OR ADVICE FROM REDA LABS, LLC WILL CONSTITUTE A WARRANTY. REDA LABS, LLC ASSUMES NO LIABILITY FOR (I) THE ACCURACY OR COMPLETENESS OF CONTENT ON THIS SITE OR ANY LINKED SITE, (II) THE OPERATION OF THIS SITE OR SERVICES, OR (III) ANY DAMAGES ARISING FROM YOUR USE OF THE SITE OR SERVICES.
            </p>
          </div>

          <div className="terms-section">
            <h2>Limitation of Liability</h2>
            <p>
              IN NO EVENT SHALL REDA LABS, LLC OR ITS AFFILIATES BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, PUNITIVE, OR CONSEQUENTIAL DAMAGES ARISING FROM YOUR USE OF THIS SITE OR SERVICES, WHETHER BASED ON WARRANTY, CONTRACT, TORT, OR OTHER LEGAL THEORY. REDA LABS, LLC'S TOTAL AGGREGATE LIABILITY SHALL NOT EXCEED $10,000. THIS LIMITATION SHALL SURVIVE TERMINATION OF THIS AGREEMENT.
            </p>
          </div>

          <div className="terms-section">
            <h2>Indemnity</h2>
            <p>
              You agree to indemnify, defend, and hold harmless Reda Labs, LLC and its affiliates from any claims, demands, damages, or losses arising from your use of this Site or Services, your violation of this Agreement, or infringement on third-party rights.
            </p>
          </div>

          <div className="terms-section">
            <h2>Compliance with Local Laws</h2>
            <p>
              Reda Labs, LLC makes no representation that Site content or Services are appropriate in every country or jurisdiction. Users accessing the Site or Services from countries where its content is illegal do so on their own initiative and are responsible for compliance with local laws.
            </p>
          </div>

          <div className="terms-section">
            <h2>Disputes, Binding Arbitration, and Waiver of Class Actions</h2>
            <p>
              Any dispute or claim arising from this Agreement shall be resolved through individual arbitration as governed by the Federal Arbitration Act (9 U.S.C. §1, et seq.). You and Reda Labs, LLC agree to resolve disputes through binding arbitration, waiving the right to a court proceeding. Individual actions within a small claims court's jurisdiction may be filed in small claims court. You agree to resolve disputes individually, waiving any right to a class action or representative proceeding. For disputes under $75,000, Reda Labs, LLC will reimburse filing fees and pay the arbitrator's fees if you reject Reda Labs, LLC's final settlement offer and receive a more favorable award. You may opt out of arbitration within 30 days of accepting this Agreement by emailing <a href="mailto:support@redalabs.com">support@redalabs.com</a>.
            </p>
          </div>

          <div className="terms-section">
            <h2>Titles, Headings, and Severability</h2>
            <p>
              The titles and headings in this Agreement are for reference only. Each provision in this Agreement is a separate covenant. If a court finds any provision illegal, invalid, or unenforceable, the remaining provisions remain in full force.
            </p>
          </div>

          <div className="terms-section">
            <h2>U.S. Export Laws</h2>
            <p>
              This Site and the Services comply with U.S. Export Laws. Users may not access the Site or Services from embargoed countries or allow access to anyone on a denied persons list.
            </p>
          </div>

          <div className="terms-section">
            <h2>English Language Controls</h2>
            <p>
              This Agreement is executed in English. Any provided translation is for convenience, with the English version prevailing in case of conflict.
            </p>
          </div>

          <div className="terms-section">
            <h2>Contact Information</h2>
            <p>
              For questions about this Agreement, contact us by email at <a href="mailto:support@redalabs.com">support@redalabs.com</a>
            </p>
          </div>
        </section>
      </div>

      <footer className="footer">
        <nav className="footer-nav">
          <Link to="/">Home</Link>
          <span className="separator">|</span>
          <Link to="/terms">Terms of Service</Link>
          <span className="separator">|</span>
          <Link to="/privacy">Privacy Policy</Link>
          <span className="separator">|</span>
          <Link to="/support">Support</Link>
        </nav>
        <p className="copyright">© 2025 Reda Labs, LLC</p>
      </footer>
    </div>
  );
}

export default TermsOfService;
